// Builds the `europass` CV variant as a docx Document, mirroring EuropassPdf.jsx
// (logo header, grey identity band, introduction, experiences, diplomas,
// languages, business skills, hobbies, footer). Verdana, 20px page margins.

import { Document, LineRuleType } from "docx";
import { extractResumeDataDocumentSection } from "../../../hooks/useResumeDocumentSection";
import { extractResumeProperties } from "../../../hooks/useResumeProperties";
import { loadImage } from "../shared/wordImage";
import { pageNumberFooter, para, separateTables } from "../shared/wordHelpers";
import { FONT_VERDANA } from "../shared/wordTheme";
import { A4, ptToHalfPoints, pxToTwips } from "../shared/wordUnits";
import { EU_MARGIN } from "./euLayout";
import { buildEuropassBand } from "./sections/euHeader";
import { buildEuropassIntroduction } from "./sections/euIntroduction";
import { buildEuropassExperiences } from "./sections/euExperiences";
import { buildEuropassDiplomas } from "./sections/euDiplomas";
import { buildEuropassLanguages } from "./sections/euLanguages";
import { buildEuropassBusinessSkills } from "./sections/euBusinessSkills";
import { buildEuropassHobbies } from "./sections/euHobbies";

export async function buildEuropassWord( resumeData, translations ) {
    const data = extractResumeDataDocumentSection( resumeData );
    const props = extractResumeProperties( resumeData );
    const ctx = { data, props, translations };

    const logoImage = await loadImage( "/europass-cv.png" );
    const spacer = () => para( [], { spacing: { before: pxToTwips( 10 ), after: 0 } } );

    const children = [];
    children.push( buildEuropassBand( { ...ctx, logoImage } ) );
    children.push( ...buildEuropassIntroduction( ctx ) );
    if ( !props.isExperienceHidden ) {
        children.push( spacer(), ...buildEuropassExperiences( ctx ) );
    }
    children.push( spacer(), ...buildEuropassDiplomas( ctx ) );
    children.push( spacer(), ...buildEuropassLanguages( ctx ) );
    children.push( spacer(), ...buildEuropassBusinessSkills( ctx ) );
    children.push( spacer(), ...buildEuropassHobbies( ctx ) );

    return new Document( {
        styles: {
            default: {
                document: {
                    run: { font: FONT_VERDANA, size: ptToHalfPoints( 10 ) },
                    paragraph: { spacing: { after: 0, line: 240, lineRule: LineRuleType.AUTO } },
                },
            },
        },
        sections: [
            {
                properties: {
                    page: {
                        size: { width: A4.width, height: A4.height },
                        margin: {
                            // Top margin 0 so the grey band bleeds to the top edge.
                            top: 0,
                            right: EU_MARGIN,
                            bottom: pxToTwips( 70 ),
                            left: EU_MARGIN,
                            footer: pxToTwips( 20 ),
                        },
                    },
                },
                footers: { default: pageNumberFooter( { font: FONT_VERDANA } ) },
                children: separateTables( children ),
            },
        ],
    } );
}
